import React, { useMemo } from "react";
import { Flame, MessageCircle, Heart, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  navigateToCanonicalForumThread,
  THREAD_NAV_ORIGIN_COMMUNITY,
} from "../features/posts/canonicalThreadNavigation";

/**
 * HotThreadsPanel (Restyled to match LiveActivityFeed)
 * Avatar moved to TOP-LEFT (not sharing row with title)
 * Title gets full horizontal space
 * Actions row lowered
 * "View" text removed (arrow only)
 * Keeps your current color/settings otherwise
 */
export default function HotThreadsPanel({
  threads = [],
  title = "Hot Threads",
  subtitle = "RIGHT NOW",
  maxItems = 10,
  className = "",
  onOpenThread,
  onLike,
  onReply,
  likedPosts = new Set(),
}) {
  const navigate = useNavigate();

  /* -------------------- helpers (author/avatar) -------------------- */
  const getAuthorName = (t) =>
    t?.author_username ||
    t?.authorName ||
    t?.author ||
    t?.username ||
    t?.user?.username ||
    t?.user?.name ||
    t?.profile?.username ||
    t?.profile?.name ||
    "User";

  const getAvatar = (t) =>
    t?.author_avatar ||
    t?.authorAvatar ||
    t?.avatar ||
    t?.profile_picture ||
    t?.profilePicture ||
    t?.user?.profile_picture ||
    t?.user?.avatar ||
    t?.profile?.profile_picture ||
    t?.profile?.avatar ||
    "";

  const initials = (name) => {
    const s = String(name || "").trim();
    if (!s) return "U";
    const parts = s.split(/\s+/).slice(0, 2);
    return parts.map((p) => p?.[0]?.toUpperCase() || "").join("") || "U";
  };

  /* -------------------- scoring + selection -------------------- */
  const hotThreads = useMemo(() => {
    const now = Date.now();

    const parseDate = (v) => {
      if (!v) return null;
      const d = new Date(v);
      return Number.isFinite(d.getTime()) ? d : null;
    };

    const safeNum = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

    const score = (t) => {
      const likes = safeNum(t?.likes);
      const replies = safeNum(t?.replies);
      const last =
        parseDate(t?.updatedAt || t?.updated_at || t?.lastActivity || t?.createdAt) || null;

      const ageH = last ? (now - last.getTime()) / 36e5 : 999;
      const recency = Math.max(0, 1 - ageH / 48);

      return likes * 1.8 + replies * 2.4 + recency * 6;
    };

    const sorted = (Array.isArray(threads) ? threads : [])
      .filter((t) => t?.id)
      .slice()
      .sort((a, b) => score(b) - score(a));

    const uniq = [];
    const seen = new Set();
    for (const t of sorted) {
      const id = String(t.id);
      if (seen.has(id)) continue;
      seen.add(id);
      uniq.push(t);
      if (uniq.length >= maxItems) break;
    }

    return uniq;
  }, [threads, maxItems]);

  const openThread = (t) => {
    if (!t?.id) return;
    if (onOpenThread) return onOpenThread(t);
    navigateToCanonicalForumThread(navigate, t, { origin: THREAD_NAV_ORIGIN_COMMUNITY });
  };

  if (!hotThreads.length) return null;

  /* -------------------- STYLE (match LiveActivityFeed) -------------------- */
  const shell =
    "rounded-3xl border border-white/12 bg-black/35 backdrop-blur-2xl ring-1 ring-white/10 overflow-hidden flex flex-col";
  const headerBar = "border-b border-white/10 bg-black/25";
  const iconBox =
    "h-9 w-9 rounded-2xl border border-white/12 bg-black/25 grid place-items-center";

  const innerCard =
    "rounded-2xl border border-white/10 bg-black/25 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]";
  const innerHover = "hover:bg-black/30 hover:border-white/15 transition-colors duration-150";

  return (
    <section className={className}>
      <div className={shell}>
        {/* Header */}
        <div className={["px-4 py-3.5", headerBar].join(" ")}>
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className={iconBox}>
                <Flame className="h-[18px] w-[18px] text-emerald-200" />
              </div>

              <div className="min-w-0 leading-tight">
                <p className="text-[13px] font-semibold text-white tracking-tight truncate">
                  {title}
                </p>
                <p className="text-[11px] text-white/45 truncate">{subtitle}</p>
              </div>
            </div>

            {/* subtle indicator dot */}
            <div className="shrink-0 h-8 w-8 rounded-2xl border border-white/12 bg-black/25 grid place-items-center">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-300/85" />
            </div>
          </div>
        </div>

        {/* Body - Vertical List */}
        <div className="px-3 py-3 max-h-[480px] overflow-y-auto scrollbar-hide">
          <div className="space-y-3">
            {hotThreads.map((t) => {
              const author = getAuthorName(t);
              const avatar = getAvatar(t);
              const replies = Number(t?.replies) || 0;
              const likes = Number(t?.likes) || 0;
              const snippet = (t?.body || t?.content || t?.text || "").trim();
              const isLiked = likedPosts.has(t.id);

              return (
                <div
                  key={t.id}
                  className={["group relative", innerCard, innerHover].join(" ")}
                  role="button"
                  tabIndex={0}
                  onClick={() => openThread(t)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") openThread(t);
                  }}
                >
                  {/* left rail */}
                  <div className="pointer-events-none absolute left-0 top-0 h-full w-[2px] bg-white/14" />

                  <div className="p-3 sm:p-4">
                    {/* Top strip: Avatar (top-left) + meta + dot */}
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3 min-w-0">
                        {/* Avatar (top-left, above name/title area) */}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            openThread(t);
                          }}
                          className="shrink-0"
                          aria-label={`Open thread by ${author}`}
                        >
                          {avatar ? (
                            <img
                              src={avatar}
                              alt={author}
                              className="h-9 w-9 rounded-full object-cover object-center border border-white/12 bg-black/25"
                              loading="lazy"
                              draggable={false}
                            />
                          ) : (
                            <div
                              className="h-9 w-9 rounded-full border border-white/12 bg-black/25 grid place-items-center text-white/85 font-semibold text-[11px]"
                              aria-hidden="true"
                            >
                              {initials(author)}
                            </div>
                          )}
                        </button>

                        {/* Meta */}
                        <div className="min-w-0">
                          <p className="text-[11px] text-white/45 truncate">
                            by <span className="text-white/70 font-medium">{author}</span>
                          </p>
                        </div>
                      </div>

                      {/* Dot box */}
                      <div
                        className="shrink-0 h-8 w-8 rounded-2xl border border-white/12 bg-black/25 grid place-items-center"
                        aria-hidden="true"
                      >
                        <span className="h-1.5 w-1.5 rounded-full bg-white/25" />
                      </div>
                    </div>

                    {/* Title + snippet get FULL width now */}
                    <div className="mt-2">
                      <p className="text-[15px] font-semibold text-white/80 truncate leading-tight">
                        {t?.title || "Untitled"}
                      </p>

                      {snippet ? (
                        <p className="text-[11.5px] text-white/60 mt-1.5 line-clamp-2 leading-relaxed">
                          {snippet}
                        </p>
                      ) : (
                        <div className="mt-1.5 h-[16px]" />
                      )}
                    </div>

                    {/* Actions row lowered */}
                    <div className="mt-4 flex items-center gap-2 flex-wrap">
                      <ActionButton
                        active={isLiked}
                        activeClass="text-emerald-300"
                        title={isLiked ? "Unlike" : "Like"}
                        count={likes}
                        icon={
                          <Heart
                            className="h-4 w-4"
                            fill={isLiked ? "currentColor" : "none"}
                          />
                        }
                        onClick={(e) => {
                          e.stopPropagation();
                          onLike?.(t);
                        }}
                      />

                      <ActionButton
                        title="Replies"
                        count={replies}
                        icon={<MessageCircle className="h-4 w-4" />}
                        onClick={(e) => {
                          e.stopPropagation();
                          onReply?.(t);
                        }}
                      />

                      {/* Arrow only (no "View" text) */}
                      <button
  type="button"
  onClick={(e) => {
    e.stopPropagation();
    openThread(t);
  }}
  className={[
    "ml-auto mt-3",
    "text-cyan-400/70",
    "hover:text-cyan-300",
    "transition-colors",
    "active:translate-x-[1px]",
  ].join(" ")}
  aria-label="Open thread"
  title="Open"
>
  <ChevronRight className="h-4 w-4" />
</button>

                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

/* -------------------- shared action button (match LiveActivityFeed) -------------------- */

function ActionButton({ onClick, icon, count, title, active, activeClass = "" }) {
  const isLikeButton = activeClass.includes("emerald");

  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={[
        "inline-flex items-center gap-2",
        "rounded-xl border",
        "px-2.5 py-1.5",
        "text-[11px]",
        "transition-colors cursor-pointer",
        "active:scale-[0.98]",
        "border-white/12 bg-black/25 text-white/60 hover:text-white hover:border-white/15 hover:bg-black/30",
      ].join(" ")}
    >
      <span className={active && isLikeButton ? "text-emerald-300" : "text-white/55"}>{icon}</span>

      <span
        className={[
          "font-semibold tabular-nums",
          "text-white/85",
        ].join(" ")}
      >
        {count}
      </span>
    </button>
  );
}
